/**
 * Smart logging utility that respects admin status and environment
 * Only logs to console for:
 * - Admin/staff users
 * - Development environment
 * - When explicitly enabled via localStorage
 */

class Logger {
  constructor() {
    this.isAdmin = false;
    this.isStaff = false;
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.forceEnabled = typeof window !== 'undefined' && localStorage.getItem('enableDebugLogs') === 'true';
  }

  /**
   * Set the current user's role
   * @param {boolean} isAdmin - Whether user is an admin
   * @param {boolean} isStaff - Whether user is staff
   */
  setUserRole(isAdmin, isStaff) {
    this.isAdmin = isAdmin;
    this.isStaff = isStaff;
  }

  /**
   * Check if logging should be enabled
   * @returns {boolean}
   */
  shouldLog() {
    return this.isDevelopment || this.isAdmin || this.isStaff || this.forceEnabled;
  }

  /**
   * Log general information (only for admin/dev)
   */
  log(...args) {
    if (this.shouldLog()) {
      console.log(...args);
    }
  }

  /**
   * Log informational messages (only for admin/dev)
   */
  info(...args) {
    if (this.shouldLog()) {
      console.info(...args);
    }
  }

  /**
   * Log warnings (only for admin/dev)
   */
  warn(...args) {
    if (this.shouldLog()) {
      console.warn(...args);
    }
  }

  /**
   * Log errors (always logged - critical for debugging)
   * In production, these could be sent to an error tracking service
   */
  error(...args) {
    console.error(...args);
    // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
  }

  /**
   * Log debug information (only for admin/dev)
   */
  debug(...args) {
    if (this.shouldLog()) {
      console.debug(...args);
    }
  }

  /**
   * Admin-only logging (even in production)
   * Use for sensitive operations that should only be visible to admins
   */
  adminOnly(...args) {
    if (this.isAdmin || this.isStaff) {
      console.log('[ADMIN]', ...args);
    }
  }

  /**
   * Log table data (only for admin/dev)
   */
  table(data) {
    if (this.shouldLog()) {
      console.table(data);
    }
  }

  /**
   * Start a timer (only for admin/dev)
   */
  time(label) {
    if (this.shouldLog()) {
      console.time(label);
    }
  }

  /**
   * End a timer (only for admin/dev)
   */
  timeEnd(label) {
    if (this.shouldLog()) {
      console.timeEnd(label);
    }
  }

  /**
   * Group logs (only for admin/dev)
   */
  group(label) {
    if (this.shouldLog()) {
      console.group(label);
    }
  }

  /**
   * End a log group (only for admin/dev)
   */
  groupEnd() {
    if (this.shouldLog()) {
      console.groupEnd();
    }
  }
}

// Create singleton instance
export const logger = new Logger();

/**
 * Helper to enable debug logs temporarily
 * Usage: In browser console, type: enableDebugLogs()
 */
export const enableDebugLogs = () => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('enableDebugLogs', 'true');
    logger.forceEnabled = true;
    console.log('✅ Debug logs enabled. Refresh the page to see all logs.');
  }
};

/**
 * Helper to disable debug logs
 * Usage: In browser console, type: disableDebugLogs()
 */
export const disableDebugLogs = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('enableDebugLogs');
    logger.forceEnabled = false;
    console.log('✅ Debug logs disabled.');
  }
};

// Make helpers available globally for easy access in browser console
if (typeof window !== 'undefined') {
  window.enableDebugLogs = enableDebugLogs;
  window.disableDebugLogs = disableDebugLogs;
}

export default logger;
